import React, { useMemo, useState } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import {
  MetricDef,
  DetailKind,
  ViewModel,
  Bucket,
  AnyRow,
  ExtraColCtx,
  bandClass,
  otdBand,
  rankOf,
  sumDenom,
  sumOnTime,
  keyStatusOf,
  NEUTRAL_TEXT,
  MUTED_TEXT,
} from './logic';
import KeyStatusBadge from '../../../components/KeyStatusBadge';

/**
 * Renders one metric as a dispatcher-rows × date-columns TanTable: filterable,
 * sortable, column-hideable and exportable. The endpoint's (dispatcher, date)
 * rows are pivoted into one row object per dispatcher; each date/week bucket
 * becomes a column.
 */

// One pivoted table row: a dispatcher with the metric summarized per bucket.
interface MatrixRow {
  dispatcher_key: string;
  dispatcher_name: string;
  key_status: string;                     // customer axis only ('' elsewhere)
  // Breakdown sub-rows (one per service-failure type) shown under the axis row
  // when Show details is on. `isBreakdown` marks those child rows themselves:
  // they carry precomputed values only, so they never link, open a modal or
  // feed an aggregate.
  subRows?: MatrixRow[];
  isBreakdown?: boolean;
  values: Record<string, number | null>; // bucketKey -> summarized value
  cells: Record<string, AnyRow[]>;        // bucketKey -> raw rows (links/detail/tooltip)
  allRows: AnyRow[];                      // dispatcher's rows across all buckets
  summary: number | null;
}

interface Props {
  metric: MetricDef;
  view: ViewModel;
  showDetails: boolean;
  isDark: boolean;
  axisLabel?: string; // 'Dispatcher' (default) | 'Customer'
  // Opens the detail modal for a cell's rows (metrics with detailKind set):
  // Service Failures, OTD late deliveries, or Uncoded planned.
  onOpenDetail?: (rows: AnyRow[], kind: DetailKind) => void;
}

const MetricMatrixTable: React.FC<Props> = ({ metric, view, showDetails, isDark, axisLabel = 'Dispatcher', onOpenDetail }) => {
  // The Total row's own breakdown. The footer is a single <tr> the table owns, so
  // it cannot carry real sub-rows like the body does — instead each footer cell
  // stacks the per-type totals under the total, toggled from the Total label.
  const [footerExpanded, setFooterExpanded] = useState(false);
  // Recompute everything when the metric/view/detail toggle changes. Colors and
  // rank cutoffs are computed over the full in-range dispatcher set (matches the
  // card); TanTable row-filtering narrows footers but not the color scale.
  const { data, columns, defaultSorting, footerPanel } = useMemo(() => {
    const hasData = metric.hasData ?? ((rows: AnyRow[]) => {
      const s = metric.summarize(rows);
      return s != null && s > 0;
    });

    const rowDispatchers = view.dispatchers.filter((d) => hasData(view.byDispatcher.get(d.key) ?? []));

    // Pivot: one row object per dispatcher.
    const data: MatrixRow[] = rowDispatchers.map((disp) => {
      const allRows = view.byDispatcher.get(disp.key) ?? [];
      const values: Record<string, number | null> = {};
      const cells: Record<string, AnyRow[]> = {};
      for (const b of view.buckets) {
        const cr = view.byCell.get(`${disp.key}|${b.key}`) ?? [];
        cells[b.key] = cr;
        values[b.key] = cr.length > 0 ? metric.summarize(cr) : (metric.emptyValue ?? null);
      }
      // Show details: one sub-row per breakdown label (service-failure types).
      // Values are precomputed per bucket; cells/allRows stay empty so a
      // sub-row can never contribute to a footer or open a detail modal.
      const subRows: MatrixRow[] | undefined = showDetails && metric.breakdown
        ? metric.breakdown.labels(allRows).map((label) => {
            const subValues: Record<string, number | null> = {};
            const subCells: Record<string, AnyRow[]> = {};
            for (const b of view.buckets) {
              const cr = view.byCell.get(`${disp.key}|${b.key}`) ?? [];
              subValues[b.key] = metric.breakdown!.value(cr, label);
              // Rows narrowed to this type, so the number stays clickable and
              // the modal it opens shows only this type's failures.
              subCells[b.key] = metric.breakdown!.filter(cr, label);
            }
            return {
              dispatcher_key: `${disp.key}|${label}`,
              dispatcher_name: label,
              key_status: '',
              isBreakdown: true,
              values: subValues,
              cells: subCells,
              allRows: metric.breakdown!.filter(allRows, label),
              summary: metric.breakdown!.value(allRows, label),
            };
          })
        : undefined;

      return {
        dispatcher_key: disp.key,
        dispatcher_name: disp.name,
        key_status: keyStatusOf(allRows),
        subRows,
        values,
        cells,
        allRows,
        summary: metric.summarize(allRows),
      };
    });
    // Customer axis: the backend stamps every customer row with its key status
    // (AVERAGE included), so any status at all means "this is the customer table".
    const hasKeyStatus = data.some((r) => r.key_status !== '');

    // Rank shading needs the column's value distribution (relative/pct metrics).
    const useRank = metric.colorMode === 'relative' || metric.colorMode === 'pct';
    const bucketValues: Record<string, number[]> = {};
    if (useRank) {
      for (const b of view.buckets) {
        const vals: number[] = [];
        for (const r of data) if (r.values[b.key] != null) vals.push(r.values[b.key] as number);
        bucketValues[b.key] = vals;
      }
    }
    const summaryValues: number[] = data.map((r) => r.summary).filter((s): s is number => s != null);

    // ---- cell background class helpers (mirror the card's cellStyle/rankStyle) ----
    const rankClass = (values: number[], val: number | null): string => {
      const cls = rankOf(values, val, metric.sortAsc);
      return cls == null ? NEUTRAL_TEXT : bandClass(cls === 'top' ? 'good' : 'bad');
    };
    // The card's cellStyle: binary/threshold/none. Used for non-rank cells.
    const staticClass = (val: number | null): string => {
      if (metric.colorMode === 'binary') {
        const bad = (val ?? 0) > 0;
        if (!bad && metric.zeroNeutral) return NEUTRAL_TEXT;
        return bandClass(bad ? 'bad' : 'good');
      }
      if (val == null) return MUTED_TEXT;
      if (metric.colorMode === 'threshold') return bandClass(otdBand(Math.round(val)));
      return NEUTRAL_TEXT;
    };
    const bucketClass = (bucketKey: string, val: number | null): string =>
      useRank ? rankClass(bucketValues[bucketKey] ?? [], val) : staticClass(val);
    // Summary cell: rank unless threshold/noSummaryRank (then static band).
    const summaryClass = (summary: number | null): string =>
      metric.colorMode !== 'threshold' && !metric.noSummaryRank
        ? rankClass(summaryValues, summary)
        : staticClass(summary);

    // ---- cell content (value + optional detail subtext + pro link + tooltip) ----
    const renderVal = (val: number | null, rowsForLink: AnyRow[], isBreakdown = false): React.ReactNode => {
      // A breakdown line for a bucket the type never occurred in shows nothing,
      // rather than a 0 competing with the real counts around it.
      if (isBreakdown && val == null) return '';
      const main = metric.format(val);
      const sub = showDetails && metric.detail ? metric.detail(rowsForLink) : null;
      const tooltip = metric.cellTooltip ? metric.cellTooltip(rowsForLink) : null;
      // `whitespace-pre-line` so a multi-line subtext (the service-failure type
      // breakdown, one type per line) renders its newlines instead of inheriting
      // the truncating cell wrapper's nowrap and being clipped to one line.
      const content: React.ReactNode = sub != null ? (
        <span className="inline-flex flex-col leading-tight">
          <span>{main}</span>
          <span className="text-[0.5625rem] font-normal opacity-65 whitespace-pre-line">{sub}</span>
        </span>
      ) : main;
      // Service Failures / OTD: open the detail modal instead of linking to
      // search (the modal itself offers the "open in search" link). SF cells
      // are clickable when the count > 0; OTD cells when the cell has late
      // deliveries (a 100% cell has nothing to show).
      if (metric.detailKind && onOpenDetail) {
        // Clickable only when there is something behind it — which is what the
        // metric's own linkPros answers. The value cannot stand in for that: a
        // rate metric is non-zero precisely when it has the FEWEST rows to show
        // (a 100% Service Pass cell has no failing pros at all), so keying off
        // the number opened empty modals on every clean cell.
        const hasDetail = metric.linkPros
          ? metric.linkPros(rowsForLink).length > 0
          : (val ?? 0) > 0;
        if (hasDetail) {
          return (
            <button
              type="button"
              onClick={() => onOpenDetail(rowsForLink, metric.detailKind!)}
              title={tooltip ?? undefined}
              className="underline cursor-pointer bg-transparent border-0 p-0"
              style={{ color: 'inherit', font: 'inherit' }}
            >
              {content}
            </button>
          );
        }
        return tooltip != null ? <span title={tooltip}>{content}</span> : content;
      }
      if (metric.linkPros) {
        const raw = metric.linkPros(rowsForLink);
        const pros = metric.linkProsDedupe === false ? raw : Array.from(new Set(raw));
        if (pros.length > 0) {
          return (
            <a
              href={`/searchresults?q=${pros.join(',')}&multi=true`}
              target="_blank"
              rel="noopener noreferrer"
              title={tooltip ?? undefined}
              style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {content}
            </a>
          );
        }
      }
      return tooltip != null ? <span title={tooltip}>{content}</span> : content;
    };

    // ---- Total-row breakdown (footer) ------------------------------------
    const canBreakDown = !!(showDetails && metric.breakdown);
    const footerRows = (table: any): AnyRow[] =>
      table.getFilteredRowModel().rows.flatMap((r: any) => (r.original as MatrixRow).allRows);
    /**
     * A value cell in the Total row: the total, plus one per-type total stacked
     * under it when the Total label is expanded. Each of those is clickable and
     * opens the detail modal narrowed to that type, like the body rows.
     */
    const footerStack = (main: React.ReactNode, rows: AnyRow[], table: any): React.ReactNode => {
      if (!canBreakDown || !footerExpanded) return main;
      return (
        <span className="inline-flex flex-col leading-tight items-center">
          <span>{main}</span>
          {metric.breakdown!.labels(footerRows(table)).map((l) => {
            const v = metric.breakdown!.value(rows, l);
            return (
              <span key={l} className="text-[0.5625rem] font-normal opacity-65">
                {v == null ? ' ' : renderVal(v, metric.breakdown!.filter(rows, l), true)}
              </span>
            );
          })}
        </span>
      );
    };

    // ---- OTD extra-column context (company roll-up + data-bar scaling) ----
    const companyTotal = metric.extraCols ? sumDenom(view.allVisible) : 0;
    const companyOnTime = metric.extraCols ? sumOnTime(view.allVisible) : 0;
    const companyAvg = companyTotal > 0 ? companyOnTime / companyTotal : 0;
    let extraCtx: ExtraColCtx | null = null;
    if (metric.extraCols) {
      let maxDrag = 0;
      let maxMvp = 0;
      for (const r of data) {
        if (companyTotal <= 0) continue;
        const drag = ((sumDenom(r.allRows) - sumOnTime(r.allRows)) / companyTotal) * 100;
        const mvp = Math.abs(sumOnTime(r.allRows) - companyAvg * sumDenom(r.allRows));
        if (drag > maxDrag) maxDrag = drag;
        if (mvp > maxMvp) maxMvp = mvp;
      }
      extraCtx = {
        isDark, companyTotal, companyOnTime, maxDrag, maxMvp,
        // Any metric with a detail modal gets it here too, so an extra column's
        // count (OTD's Late, Service %'s Failed) opens the same modal the cells
        // do instead of dropping the user into a search tab.
        onOpenDetail: metric.detailKind && onOpenDetail
          ? (rows: AnyRow[]) => onOpenDetail(rows, metric.detailKind!)
          : undefined,
      };
    }

    // ---- columns ----
    const columns: ColumnDef<MatrixRow>[] = [];

    // Name column (pinned left via lockedColumns; filterable + sortable).
    columns.push({
      id: 'dispatcher_name',
      accessorKey: 'dispatcher_name',
      header: axisLabel,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      size: 190,
      // No cellBgFn — rank shading is carried by the Total/Avg column; the name
      // column stays plain.
      meta: {
        exportValue: (row: MatrixRow) => row.dispatcher_name,
      },
      cell: ({ row }) => {
        const tip = metric.nameTooltip ? metric.nameTooltip(row.original.allRows) : null;
        return <span title={tip ?? undefined}>{row.original.dispatcher_name}</span>;
      },
      footer: ({ table }) => {
        if (!canBreakDown) return <span className="font-semibold">{metric.summaryLabel}</span>;
        // Labels come from the same filtered rows the numbers do, so the stack
        // in this cell lines up with the stacks in the value columns.
        const labels = footerExpanded ? metric.breakdown!.labels(footerRows(table)) : [];
        return (
          <span className="inline-flex flex-col leading-tight items-start">
            <button
              type="button"
              onClick={() => setFooterExpanded((v) => !v)}
              className="font-semibold bg-transparent border-0 p-0 cursor-pointer inline-flex items-center gap-1"
              style={{ color: 'inherit', font: 'inherit' }}
              title={footerExpanded ? 'Hide the failure-type totals' : 'Break the total down by failure type'}
            >
              <span className="text-[0.625rem] opacity-70">{footerExpanded ? '▾' : '▸'}</span>
              <span className="font-semibold">{metric.summaryLabel}</span>
            </button>
            {labels.map((l) => (
              <span key={l} className="text-[0.5625rem] font-normal opacity-65 pl-3">{l}</span>
            ))}
          </span>
        );
      },
    });

    // Customer axis only: the key status in its own column, right after the name
    // — sortable, filterable and exportable like any other, with the tier pill
    // for key accounts and nothing drawn for the AVERAGE default.
    if (hasKeyStatus) {
      columns.push({
        id: 'key_status',
        accessorKey: 'key_status',
        header: 'Key Status',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: {
          exportValue: (row: MatrixRow) => row.key_status,
        },
        cell: ({ row }) => <KeyStatusBadge status={row.original.key_status} />,
      });
    }

    // One column per date / week bucket.
    for (const b of view.buckets as Bucket[]) {
      columns.push({
        id: b.key,
        // null (no data) -> undefined so sortUndefined sinks empty cells.
        accessorFn: (row: MatrixRow) => row.values[b.key] ?? undefined,
        header: () => (
          <div className="text-center leading-tight">
            <div>{b.label}</div>
            <div className="text-[0.625rem] opacity-70">{b.sub}</div>
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        size: 58,
        sortUndefined: 'last',
        meta: {
          align: 'center',
          // Breakdown rows stay uncoloured — every type count would otherwise
          // take the parent's banding and drown the row it explains.
          cellBgFn: (_val: unknown, row: MatrixRow) =>
            row.isBreakdown ? '' : bucketClass(b.key, row.values[b.key]),
          exportValue: (row: MatrixRow) => (row.values[b.key] == null ? '' : row.values[b.key]),
        },
        cell: ({ row }) => renderVal(row.original.values[b.key], row.original.cells[b.key] ?? [], row.original.isBreakdown),
        footer: ({ table }) => {
          const rows = table.getFilteredRowModel().rows.flatMap((r) => (r.original as MatrixRow).cells[b.key] ?? []);
          return footerStack(renderVal(metric.summarize(rows), rows), rows, table);
        },
      });
    }

    // Total / Avg summary column.
    columns.push({
      id: 'summary',
      accessorFn: (row: MatrixRow) => row.summary ?? undefined,
      header: metric.summaryLabel,
      enableSorting: true,
      enableColumnFilter: false,
      size: 66,
      sortUndefined: 'last',
      meta: {
        align: 'center',
        cellBgFn: (_val: unknown, row: MatrixRow) => (row.isBreakdown ? '' : summaryClass(row.summary)),
        exportValue: (row: MatrixRow) => (row.summary == null ? '' : row.summary),
      },
      cell: ({ row }) => renderVal(row.original.summary, row.original.allRows, row.original.isBreakdown),
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows.flatMap((r) => (r.original as MatrixRow).allRows);
        return footerStack(renderVal(metric.summarize(rows), rows), rows, table);
      },
    });

    // OTD extra columns (Delivered / Late / Impact(−) / Impact(+)).
    for (const ec of metric.extraCols ?? []) {
      columns.push({
        id: ec.key,
        accessorFn: (row: MatrixRow) => (ec.sortValue ? ec.sortValue(row.allRows, companyAvg) : 0),
        header: () => ec.header,
        enableSorting: !!ec.sortValue,
        enableColumnFilter: false,
        size: ec.minWidth ?? 90,
        sortUndefined: 'last',
        meta: {
          align: 'center',
          exportValue: (row: MatrixRow) => (ec.sortValue ? ec.sortValue(row.allRows, companyAvg) : ''),
        },
        cell: ({ row }) => (extraCtx ? ec.cell(row.original.allRows, extraCtx) : null),
        footer: ec.footer
          ? ({ table }) => {
              const rows = table.getFilteredRowModel().rows.flatMap((r) => (r.original as MatrixRow).allRows);
              return extraCtx && ec.footer ? ec.footer(rows, extraCtx) : null;
            }
          : undefined,
      });
    }

    // Default ordering: OTD by late-count impact desc; every other metric by
    // its Total/Avg descending (highest first — incl. Service Failures and
    // Uncoded, which rank best-first for coloring but sort worst-first here).
    const hasDrag = !!metric.extraCols?.some((c) => c.key === 'otd_drag');
    const defaultSorting: SortingState = hasDrag
      ? [{ id: 'otd_drag', desc: true }]
      : [{ id: 'summary', desc: true }];

    const footerPanel = metric.footerPanel && extraCtx ? metric.footerPanel(view.allVisible, extraCtx) : null;

    return { data, columns, defaultSorting, footerPanel };
  }, [metric, view, showDetails, isDark, axisLabel, onOpenDetail, footerExpanded]);

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-sm text-gray-400 dark:text-gray-500">No {metric.label.toLowerCase()} data in this range.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-h-0 gap-3">
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <TanTable
          data={data}
          columns={columns}
          loading={false}
          getRowId={(r) => r.dispatcher_key}
          // Show details: the failure-type breakdown rows under each axis row,
          // COLLAPSED to start. Expanded by default the page opened as a wall of
          // rows and the axis you came to read was buried; the chevron opens the
          // one dispatcher / customer / LC you are actually looking at.
          {...(showDetails && metric.breakdown
            ? { getSubRows: (r: MatrixRow) => r.subRows, subRowsGroupLabel: 'Failure types' }
            : {})}
          lockedColumns={[{ id: 'dispatcher_name', side: 'left' }]}
          defaultSorting={defaultSorting}
          // These tables have a dynamic (date-driven) column set, so persisting
          // per-user layout goes stale and fights the defaults. Disable it — order,
          // sort, visibility and filters always come from the props/defaults. (The
          // page remounts this component when the column set changes, so the default
          // chronological order is always rebuilt.)
          settingsKey={`dispatcher-metrics-${metric.key}`}
          disableSettingsPersistence
          enableColumnEditing={false}
          enableColumnPinning={false}
          cardTitle={metric.label}
          searchPlaceholder={`Search ${axisLabel.toLowerCase()}s...`}
          enableColumnFilters
          enableSearch
          enableExcelExport
          showColumnFooter
          fillContainer
          defaultPageSize={500}
        />
      </div>
      {footerPanel && <div className="flex-shrink-0 overflow-y-auto py-1">{footerPanel}</div>}
    </div>
  );
};

export default MetricMatrixTable;
